import { ROLE_PRIORITY, SYSTEM_ROLES, type SystemRole } from '../rbac/roles';
import { APPLICATION_KEYS, type ApplicationKey } from './application-keys';

export interface ApplicationRoles {
  key: ApplicationKey;
  name: string;
  description: string;
  roles: readonly SystemRole[];
  /** Roles that stay in the code for now but are being removed: nobody should be given one. */
  retiring?: boolean;
}

/**
 * Which application each system role belongs to. Until phase 2 of
 * docs/platform/access-model/plan.md moves roles into per-application catalogues in
 * contracts, this is the one place the grouping is written down; both the
 * catalogue and the Access card on a person read it.
 */
export const APPLICATION_ROLES: readonly ApplicationRoles[] = [
  {
    key: APPLICATION_KEYS.platform,
    name: 'Platform',
    description: 'People, access and the directory sync.',
    roles: [SYSTEM_ROLES.platformAdmin],
  },
  {
    key: APPLICATION_KEYS.projectManagement,
    name: 'Project Management',
    description: 'Clients, statements of work, projects, allocation and utilisation.',
    roles: [
      SYSTEM_ROLES.pmo,
      SYSTEM_ROLES.deliveryManager,
      SYSTEM_ROLES.projectManager,
      SYSTEM_ROLES.teamMember,
      SYSTEM_ROLES.viewer,
    ],
  },
  {
    key: APPLICATION_KEYS.onboarding,
    name: 'Onboarding',
    description: 'Candidates, the hiring workflow, signing and the employee record.',
    roles: [SYSTEM_ROLES.hrAdmin, SYSTEM_ROLES.hrSpecialist],
  },
  {
    key: APPLICATION_KEYS.training,
    name: 'Training',
    description: 'Questionnaires, assignments, attempts and certificates.',
    roles: [SYSTEM_ROLES.trainingAdmin, SYSTEM_ROLES.trainingAuthor],
  },
  {
    key: APPLICATION_KEYS.syncConsole,
    name: 'Sync console',
    description: 'Zoho sync runs and the events each one wrote.',
    roles: [SYSTEM_ROLES.maintainer],
  },
  {
    key: APPLICATION_KEYS.retiring,
    name: 'Org Admin',
    description: 'Kept for the people who hold it today; not to be granted to anyone new.',
    roles: [SYSTEM_ROLES.orgAdmin],
    retiring: true,
  },
];

/** Where roles the mapping does not know about (a custom one) are listed, so none is hidden. */
export const OTHER_APPLICATION: ApplicationRoles = {
  key: APPLICATION_KEYS.other,
  name: 'Other',
  description: 'Roles outside the platform’s own applications.',
  roles: [],
};

export const applicationOfRole = (roleName: string): ApplicationRoles | undefined =>
  APPLICATION_ROLES.find((application) =>
    (application.roles as readonly string[]).includes(roleName),
  );

/**
 * Which role a sign-in activates. `requestedRole` (a refresh keeping what the session
 * had, or an explicit switch) wins when still held. Otherwise the person's best role
 * in the application the sign-in started on; then their chosen default if still held;
 * then plain priority over everything; then none.
 *
 * `defaultRole` is honoured only while held, so a stale value can never grant a role.
 * The retiring group is never a target: nobody signs in "to" Org Admin.
 */
export interface ActiveRoleInput {
  heldRoles: readonly SystemRole[];
  requestedRole?: SystemRole;
  targetApplication?: string;
  defaultRole?: SystemRole | null;
}

export const resolveActiveRole = ({
  heldRoles,
  requestedRole,
  targetApplication,
  defaultRole,
}: ActiveRoleInput): SystemRole | null => {
  if (requestedRole && heldRoles.includes(requestedRole)) return requestedRole;

  if (targetApplication && targetApplication !== APPLICATION_KEYS.retiring) {
    const inTarget = ROLE_PRIORITY.find(
      (role) => heldRoles.includes(role) && applicationOfRole(role)?.key === targetApplication,
    );

    if (inTarget) return inTarget;
  }

  if (defaultRole && heldRoles.includes(defaultRole)) return defaultRole;

  return ROLE_PRIORITY.find((role) => heldRoles.includes(role)) ?? null;
};
